/**
 * The belt and points system.
 *
 * PORTED VERBATIM from taskdojo-app/src/utils/score.js. Keep it that way: the
 * two files must agree, or a user's belt differs between their phone and the
 * web and neither number is trustworthy. Only the type annotations below differ
 * from the source, so a diff against the mobile file stays readable.
 *
 * If this needs changing, change the mobile file first and re-port, rather than
 * editing here and hoping the other side follows. Three separate date bugs on
 * mobile today came from the same logic living in more than one place.
 */
/* eslint-disable */

export interface Belt { name: string; at: number; color: string; }

export interface ComputeScoreArgs {
  /** 'YYYY-MM-DD' -> habit ids completed that day */
  completionsByDay?: Record<string, string[]>;
  habits?: Array<{ id: string; createdAt?: any }>;
  completedTasks?: number;
  startedTasks?: number;
  now?: Date;
}

export interface ScoreResult {
  score: number;
  earned: number;
  lost: number;
  rawLost: number;
  days: number;
}

export const PENALTY_WINDOW_DAYS = 90;

/** A lapse can cost at most this share of what you have earned. */
export const MAX_PENALTY_SHARE = 0.4;

export const POINTS = {
  habitDone: 10,
  habitMissed: -4,
  taskDone: 15,
  taskStarted: 1,
};

/** Belt thresholds in points. See the header for the pacing this implies. */
export const BELTS = [
  { name: 'White',  at: 0,     color: '#EDE7DA' },
  { name: 'Yellow', at: 250,   color: '#FEC200' },
  { name: 'Orange', at: 750,   color: '#C1763C' },
  { name: 'Green',  at: 1750,  color: '#4C9A2A' },
  { name: 'Blue',   at: 3500,  color: '#4A6FA5' },
  { name: 'Brown',  at: 6500,  color: '#8B5A2B' },
  { name: 'Black',  at: 11000, color: '#2A3D47' },
];

/** Local YYYY-MM-DD, matching the keys used for the completion documents. */
export const dayKey = (d: Date = new Date()): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const toDate = (v: any): Date | null => {
  if (!v) return null;
  if (typeof v?.toDate === 'function') return v.toDate();      // Firestore Timestamp
  if (typeof v?.seconds === 'number') return new Date(v.seconds * 1000);
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** 'YYYY-MM-DD' back to a local Date, for dating a habit by its first completion. */
function fromDayKey(key: string): Date | null {
  const [y, m, d] = String(key).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

/**
 * Score from the full history.
 *
 * @param completionsByDay  { 'YYYY-MM-DD': string[] }  habit ids completed
 * @param habits            [{ id, createdAt }]         habits that exist now
 * @param completedTasks    number
 * @param startedTasks      number  tasks ever moved off 'not started'
 * @param now               Date, for testing
 */
export function computeScore({ completionsByDay = {}, habits = [], completedTasks = 0, startedTasks = 0, now = new Date() }: ComputeScoreArgs): ScoreResult {
  const keys = Object.keys(completionsByDay);

  // A habit only counts against you once you have actually started it. A habit
  // created and never begun should not quietly bill you every day it exists.
  //
  // The set is built from the completion history rather than the habits that
  // exist right now, and that is deliberate: deleting a habit used to erase
  // every penalty it had accrued, so the score jumped upward the moment you
  // removed something you had been missing -- the fastest way to gain points
  // was to delete what you were failing. Reading history instead means a
  // deleted habit stops accruing new penalties but keeps the ones it already
  // has, and those age out of the 90-day window on their own.
  //
  // A habit's start date is its first recorded completion. For a live habit
  // that is on or after createdAt, and for a deleted one it is all that is
  // left -- either way nothing is charged for days before the habit was real.
  const firstDone = new Map();
  keys.sort().forEach((k) => {
    (completionsByDay[k] || []).forEach((id) => {
      if (!firstDone.has(id)) firstDone.set(id, fromDayKey(k));
    });
  });

  const live = new Map(habits.filter((h) => h.id).map((h) => [h.id, toDate(h.createdAt)]));
  const started = [...firstDone.entries()].map(([id, at]) => ({
    id,
    // Prefer the recorded creation date where the habit still exists; fall back
    // to its first completion for one that has been deleted.
    at: live.has(id) && live.get(id) ? live.get(id) : at,
  }));

  // Earning is lifetime and permanent: work you did is never taken back.
  let doneCount = 0;
  keys.forEach((k) => { doneCount += (completionsByDay[k] || []).length; });
  // Starting something counts for a little, once per task ever. It is keyed on
  // a permanent startedAt stamp rather than the current status, so finishing a
  // task keeps the point rather than moving off 'in progress' and taking it
  // back. Deliberately small: getting started is worth acknowledging, finishing
  // is worth fifteen times more.
  const earned = doneCount * POINTS.habitDone
    + (Number(completedTasks) || 0) * POINTS.taskDone
    + (Number(startedTasks) || 0) * POINTS.taskStarted;

  // Penalties look only at the recent past. Charging back to a habit's creation
  // meant a long-dormant account could owe thousands of points before it had a
  // chance to earn any, which is punishment rather than encouragement.
  let raw = 0;
  let days = 0;
  const todayKey = dayKey(now);
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const cursor = new Date(end);
  cursor.setDate(cursor.getDate() - (PENALTY_WINDOW_DAYS - 1));

  while (cursor <= end) {
    const key = dayKey(cursor);
    // Today is still in progress, so a habit not done *yet* is not a miss.
    if (key !== todayKey) {
      const done = new Set(completionsByDay[key] || []);
      const dayEnd = new Date(cursor.getTime() + 86399999);
      const expected = started.filter((h) => !h.at || h.at <= dayEnd);
      let hit = 0;
      expected.forEach((h) => { if (done.has(h.id)) hit += 1; });
      raw += (expected.length - hit) * -POINTS.habitMissed;
      days += 1;
    }
    cursor.setDate(cursor.getDate() + 1);
  }

  // A lapse should cost you real ground — enough to drop a belt — without
  // erasing the work behind it. Capping the charge at a share of what you have
  // earned keeps the number honest and the user in the game.
  const lost = Math.min(raw, Math.round(earned * MAX_PENALTY_SHARE));

  const score = Math.max(0, earned - lost);
  return { score, earned, lost, rawLost: raw, days };
}

/** Current belt, the next one, and progress between them. */
export function beltForScore(score: number = 0) {
  const n = Math.max(0, Math.floor(score) || 0);
  let i = 0;
  for (let k = 0; k < BELTS.length; k++) if (n >= BELTS[k].at) i = k;

  const belt = BELTS[i];
  const next = BELTS[i + 1] || null;
  const percent = next
    ? Math.min(100, Math.round(((n - belt.at) / (next.at - belt.at)) * 100))
    : 100;

  return { belt, next, percent, score: n, index: i };
}

/** What a fully missed day costs, for explaining the rules in the UI. */
export function dailyStake(habitCount: number = 0) {
  return {
    max: habitCount * POINTS.habitDone,
    risk: habitCount * -POINTS.habitMissed,
  };
}
